/**
 * Utilidad de firma HMAC-SHA256
 * Sprint 3 - Para firmas de dispositivo en requests
 * 
 * Usa Web Crypto API (disponible en navegadores modernos y Capacitor)
 */

/**
 * Convierte un string a ArrayBuffer para uso con Web Crypto API
 */
function stringToArrayBuffer(str: string): Uint8Array {
    const encoder = new TextEncoder();
    return encoder.encode(str);
}

/**
 * Convierte ArrayBuffer a string hexadecimal
 */
function arrayBufferToHex(buffer: ArrayBuffer): string {
    const byteArray = new Uint8Array(buffer);
    return Array.from(byteArray)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Calcula HMAC-SHA256 de un mensaje
 * 
 * @param secret - El secreto para la firma
 * @param message - El mensaje a firmar
 * @returns La firma en formato hexadecimal (64 caracteres)
 */
export async function hmacSHA256(secret: string, message: string): Promise<string> {
    // Importar la clave secreta
    const keyData = stringToArrayBuffer(secret);
    const key = await crypto.subtle.importKey(
        'raw',
        keyData.buffer as ArrayBuffer,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );

    // Calcular la firma
    const messageData = stringToArrayBuffer(message);
    const signature = await crypto.subtle.sign('HMAC', key, messageData.buffer as ArrayBuffer);

    // Convertir a hex
    return arrayBufferToHex(signature);
}

/**
 * Genera firma para login
 * Message format: "{device_uuid}.{timestamp}"
 */
export async function generateLoginSignature(
    deviceSecret: string,
    deviceUuid: string,
    timestamp: number
): Promise<string> {
    const message = `${deviceUuid}.${timestamp}`;
    return hmacSHA256(deviceSecret, message);
}

/**
 * Genera firma para bind
 * Message format: "{device_uuid}.{nonce}.{timestamp}"
 */
export async function generateBindSignature(
    deviceSecret: string,
    deviceUuid: string,
    nonce: string,
    timestamp: number
): Promise<string> {
    const message = `${deviceUuid}.${nonce}.${timestamp}`;
    return hmacSHA256(deviceSecret, message);
}

/**
 * Genera firma para requests autenticados (middleware)
 * Message format: "{METHOD}.{path}.{timestamp}"
 */
export async function generateRequestSignature(
    deviceSecret: string,
    method: string,
    path: string,
    timestamp: number
): Promise<string> {
    const message = `${method.toUpperCase()}.${path}.${timestamp}`;
    return hmacSHA256(deviceSecret, message);
}
